import { AssertionDescriptorValueDisplayTypes } from '../utilities/Constants';
import moment from 'moment';

export default class Common {
    static currentLocalTime() {
        return moment().format('HH:mm:ss sss');
    }

    static currentUTCTime() {
        return moment().utc().format('YYYY-MM-DD HH:mm:ss');
    }

    static currentDate() {
        return moment().format('YYYY-MM-DD');
    }

    static currentDateTime() {
        return moment().format('YYYY-MM-DD HH:mm');
    }

    static dateFormat(date?: string) {
        if (date && date.length > 0) {
            return moment(date).format('YYYY-MM-DD');
        }
        return '';
    }

    static safeDateFormatOrUndefined(dateValue: any): any {
        if (dateValue && Common.isValidDate(dateValue)) {
            return Common.dateFormat(dateValue);
        }
        return undefined;
    }

    static safeDateFormat(dateValue: any): string {
        if (dateValue && Common.isValidDate(dateValue)) {
            return Common.dateFormat(dateValue);
        }
        return '';
    }

    static safeDateFormatWithDependant(dateValue: any, dependedField: any): string {
        if (dependedField) {
            return Common.safeDateFormat(dateValue);
        }
        return '';
    }

    static dateTimeFormat(date?: string) {
        if (date && date.length > 0) {
            return moment(date).format('YYYY-MM-DD HH:mm');
        }
        return '';
    }

    static dateTimeFormatToLocal(date?: string) {
        if (date && date.length > 0) {
            const minDate = moment('0001-01-01T00:00:00');
            const inputDate = moment.utc(date);

            if (!inputDate.isValid() || inputDate.isSameOrBefore(minDate)) {
                return '0001-01-01 00:00';
            }

            return inputDate.local().format('YYYY-MM-DD HH:mm');
        }
        return '';
    }

    static toUTC(date: string) {
        return moment(date).utc().format('YYYY-MM-DD HH:mm');
    }

    static localEndOfDayInUTC(date: string) {
        return moment(date).endOf('day').utc().format('YYYY-MM-DD HH:mm');
    }

    static localStartOfDayInUTC(date: string) {
        return moment(date).startOf('day').utc().format('YYYY-MM-DD HH:mm');
    }

    static getMonthAndYear(strDate: string) {
        return moment(strDate).format('MMMM YYYY');
    }

    static Year(strDate: string) {
        return moment(strDate).year();
    }

    static getStartOfCalendarYear() {
        return moment().startOf('year').format('YYYY-MM-DD');
    }

    static getEndOfCalendarYear() {
        return moment().endOf('year').format('YYYY-MM-DD');
    }

    static isMinDateTime(date: string) {
        return moment(date).year() == 1;
    }

    static formatCurrency(
        value?: string,
        excludeSymbol: boolean = false,
        handleDecimals: boolean = false
    ): string {
        if (!value) return '';

        let amount: number;

        if (handleDecimals) {
            amount = Math.round(parseFloat(value));
        } else {
            amount = parseInt(value, 10);
        }

        if (Number.isNaN(amount)) return '';

        // Format with commas, no decimals
        let formattedCurrency = Math.abs(amount).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });

        if (!excludeSymbol) {
            formattedCurrency = '$' + formattedCurrency;

            if (amount < 0) {
                formattedCurrency = '(' + formattedCurrency + ')';
            }
        }

        return formattedCurrency;
    }

    static formatCurrencyVal(value?: string, excludeSymbol?: boolean) {
        if (value) {
            const amount = parseInt(value);
            if (Number.isNaN(amount)) return '';

            let formattedCurrency = Math.abs(amount).toLocaleString();

            if (!excludeSymbol) {
                formattedCurrency = '$' + formattedCurrency;

                if (amount < 0) formattedCurrency = '(' + formattedCurrency + ')';
            }

            return formattedCurrency;
        }
        return value;
    }

    static formatCurrencyWithCents(value?: string, excludeSymbol?: boolean): string {
        if (!value) return value ?? '';

        const amount = Number(value);
        if (isNaN(amount)) return '';

        const absAmount = Math.abs(amount);

        let formatted = absAmount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        if (!excludeSymbol) {
            formatted = '$' + formatted;
            if (amount < 0) formatted = `(${formatted})`;
        }

        return formatted;
    }

    static isValidDate(strDate: string) {
        const formats = [moment.ISO_8601, 'YYYY-MM-DD'];
        return moment(strDate, formats, true).isValid();
    }

    static dateDiffInYears(strDate1: string, strDate2: string): number {
        const a = moment(strDate1);
        const b = moment(strDate2);
        return a.diff(b, 'years');
    }

    static dateDiffInDays(strDate1: string, strDate2: string): number {
        const a = moment(strDate1);
        const b = moment(strDate2);
        return a.diff(b, 'days');
    }

    static dateDiff(strDate1: string, strDate2: string): number {
        const a = moment(strDate1);
        const b = moment(strDate2);
        return a.diff(b);
    }

    static isValidPhoneNumber(str: string): boolean {
        const regEx = /^\(?([0-9]{3})\)?[-]?([0-9]{3})[-. ]?([0-9]{4})$/;
        if (str.match(regEx)) return true;
        return false;
    }

    static isValidEmail(email: string): boolean {
        const regEx =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (email.toLowerCase().match(regEx)) return true;
        return false;
    }

    static isValidUri(testString: string): boolean {
        try {
            new URL(testString);
            return true;
        } catch (err) {
            return false;
        }
    }

    static formatPhoneNumber(value: string): string {
        const phoneNumber = value.replace(/[^\d]/g, '');
        if (phoneNumber.length < 4) return phoneNumber;
        if (phoneNumber.length < 7) {
            return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
        }
        return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }

    static formatTextByDisplayType(value: string, format?: number) {
        if (format) {
            switch (format) {
                case AssertionDescriptorValueDisplayTypes.Currency.Value:
                    return this.formatCurrency(value);
                case AssertionDescriptorValueDisplayTypes.DateTime.Value:
                    return this.dateFormat(value);
                case AssertionDescriptorValueDisplayTypes.Percentage.Value:
                    return value + '%';
                default:
            }
        }
        return value;
    }

    static formatBytes(bytes: number, decimalCount: number = 2) {
        if (bytes == 0) return '0 Bytes';
        var k = 1024,
            sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
            i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(decimalCount)) + ' ' + sizes[i];
    }
}

export const EMPTY_VALUE = '__________';
